import { Injectable } from '@angular/core';
import { Node, Edge } from 'ngx-vflow';
import {
	GameData,
	GameDataIndex,
	GameDataNode,
} from '@meta-press/shared-plugins';
import {
	GameDataNodeComponent,
	GameDataNodeData,
} from '../node-editor/components/game-data-node.component';

@Injectable({
	providedIn: 'root',
})
export class NodeService {
	/**
	 * Creates nodes from GameData using the hierarchical index structure
	 */
	createNodesFromGameDataIndexes(indexes: GameDataIndex[]): Node[] {
		return indexes.map(
			(index) =>
				({
					id: index.path.join('.'),
					point: this.calculateNodePosition(index),
					type: GameDataNodeComponent,
					data: {
						gameData: index.gameData,
						path: index.path,
						displayText: this.getGameDataDisplayText(
							index.gameData,
							index.path,
						),
					} satisfies GameDataNodeData,
					width: 200,
					height: 60,
				}) as Node,
		);
	}

	/**
	 * Creates nodes from GameData tree structure
	 */
	createNodesFromGameDataTree(tree: GameDataNode): Node[] {
		const nodes: Node[] = [];
		this.collectNodesFromTree(tree, nodes);
		return nodes;
	}

	/**
	 * Creates edges showing parent-child relationships
	 */
	createEdgesFromGameDataIndexes(indexes: GameDataIndex[]): Edge[] {
		const edges: Edge[] = [];

		indexes.forEach((index) => {
			if (index.parent) {
				// Find parent index to get its path
				const parentIndex = indexes.find(
					(idx) => idx.gameData === index.parent,
				);
				if (parentIndex) {
					const sourceId = parentIndex.path.join('.');
					const targetId = index.path.join('.');

					edges.push({
						id: `${sourceId}->${targetId}`,
						source: sourceId,
						target: targetId,
						data: { color: '#64748b' },
						markers: { end: { type: 'arrow-closed' } },
					});
				}
			}
		});

		return edges;
	}

	/**
	 * Creates edges from tree structure
	 */
	createEdgesFromGameDataTree(tree: GameDataNode): Edge[] {
		const edges: Edge[] = [];
		this.collectEdgesFromTree(tree, edges);
		return edges;
	}

	private collectNodesFromTree(node: GameDataNode, nodes: Node[]): void {
		nodes.push({
			id: node.id,
			point: this.calculateNodePositionFromNode(node),
			type: GameDataNodeComponent,
			data: {
				gameData: node.gameData,
				path: node.path,
				displayText: this.getGameDataDisplayText(
					node.gameData,
					node.path,
				),
			} satisfies GameDataNodeData,
		} as Node);

		node.children.forEach((child) => {
			this.collectNodesFromTree(child, nodes);
		});
	}

	private collectEdgesFromTree(node: GameDataNode, edges: Edge[]): void {
		node.children.forEach((child) => {
			edges.push({
				id: `${node.id}->${child.id}`,
				source: node.id,
				target: child.id,
				data: { color: '#64748b' },
				markers: { end: { type: 'arrow-closed' } },
			});

			this.collectEdgesFromTree(child, edges);
		});
	}

	private calculateNodePosition(index: GameDataIndex): {
		x: number;
		y: number;
	} {
		const horizontalSpacing = 250;
		const verticalSpacing = 150;
		const levelOffset = 50;

		// Calculate x position based on path
		let x = levelOffset;
		if (index.path.length > 1) {
			// Use the path to determine horizontal position
			const siblingIndex = index.path[index.path.length - 1];
			x =
				levelOffset +
				index.depth * horizontalSpacing +
				siblingIndex * 100;
		}

		// Calculate y position based on depth
		const y = levelOffset + index.depth * verticalSpacing;

		return { x, y };
	}

	private calculateNodePositionFromNode(node: GameDataNode): {
		x: number;
		y: number;
	} {
		const horizontalSpacing = 250;
		const verticalSpacing = 150;
		const levelOffset = 50;

		// Calculate x position based on path
		let x = levelOffset;
		if (node.path.length > 1) {
			// Use the path to determine horizontal position
			const siblingIndex = node.path[node.path.length - 1];
			x =
				levelOffset +
				node.depth * horizontalSpacing +
				siblingIndex * 100;
		}

		// Calculate y position based on depth
		const y = levelOffset + node.depth * verticalSpacing;

		return { x, y };
	}

	private getGameDataDisplayText(gameData: GameData, path: number[]): string {
		// Try to get a meaningful display name from the GameData
		const typeName = gameData.constructor.name.replace('GameData', '');
		const pathStr = path.join('.');
		return `${typeName || 'GameData'} [${pathStr}]`;
	}
}
